/**
 * @file NumericalValueContent.tsx
 * @description 数值条件格式的「前景层」— 上色文字 + 左/右图标（第 4 层，可被多个 Cell 复用）。
 *
 * 【不负责】条形背景（见 BarPercentCell / BarPercentNumericalCell）。
 * 【数据】text 由 AliTableCell 经 getCellText 格式化；颜色/图标由 resolveNumericalCellPresentation 计算。
 */

import React, { useMemo } from 'react';
import classNames from 'classnames';
import SvgIcon from '@home/components/SvgIcon';

import { getNumericalIconGroupKey } from '@dbl/core/chartEditor';
import { resolveNumericalCellPresentation } from '@customComp/AliTableAnalytics/utils/numericalConditionalFormat';
import type { IAliTableCellComponentProps } from '@customComp/AliTableAnalytics/types';

const buildNumericalIconClassName = (
  placement: 'left' | 'right',
  icon?: string,
): string => {
  const groupKey = icon ? getNumericalIconGroupKey(icon) : undefined;
  return classNames(
    'dbl-table-numerical-value__icon',
    `dbl-table-numerical-value__icon--${placement}`,
    groupKey && `dbl-table-numerical-value__icon--${groupKey}`,
  );
};

export interface INumericalValueContentProps {
  /** 已 format 的展示文案，如 "10.50%" */
  text: string;
  row: IAliTableCellComponentProps['row'];
  column: IAliTableCellComponentProps['column'];
  className?: string;
}

const NumericalValueContent: React.FC<INumericalValueContentProps> = ({
  text,
  row,
  column,
  className,
}) => {
  const cell = row?.[column.code];
  /** 规则匹配用原始 value，不用 text（避免 "%" 字符串干扰比较） */
  const rawValue = cell?.value;
  const rules = column.numericalFormatConfig?.rules;

  const presentation = useMemo(
    () =>
      resolveNumericalCellPresentation(
        rules,
        rawValue,
        column.valueType,
      ),
    [rules, rawValue, column.valueType],
  );

  return (
    <span
      className={classNames('dbl-table-numerical-value', className)}
      style={{ color: presentation.textColor }}
    >
      {presentation.leftIcon ? (
        <SvgIcon
          className={buildNumericalIconClassName('left', presentation.leftIcon)}
          type={presentation.leftIcon}
        />
      ) : null}
      <span className="dbl-table-numerical-value__text">{text}</span>
      {presentation.rightIcon ? (
        <SvgIcon
          className={buildNumericalIconClassName(
            'right',
            presentation.rightIcon,
          )}
          type={presentation.rightIcon}
        />
      ) : null}
    </span>
  );
};

export default NumericalValueContent;
